import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Mail,
  X,
  Clock,
  ShieldCheck,
  User as UserIcon,
  RefreshCw,
} from "lucide-react";
import type { CompanyInvite } from "@/api/generated/client/models/CompanyInvite";

interface PendingInvitesTableProps {
  invites: CompanyInvite[];
  onCancelInvite: (inviteId: number) => void;
  onResendInvite: (inviteId: number) => void;
  isCancelling: boolean;
  isResending: boolean;
}

const getRoleBadge = (role: string) => {
  switch (role) {
    case "admin":
      return (
        <Badge variant="secondary" className="flex items-center gap-1">
          <ShieldCheck className="h-3 w-3" />
          Admin
        </Badge>
      );
    case "member":
    default:
      return (
        <Badge variant="outline" className="flex items-center gap-1">
          <UserIcon className="h-3 w-3" />
          Member
        </Badge>
      );
  }
};

export function PendingInvitesTable({
  invites,
  onCancelInvite,
  onResendInvite,
  isCancelling,
  isResending,
}: PendingInvitesTableProps) {
  return (
    <div className="overflow-x-auto md:overflow-visible">
      <Table className="min-w-[720px] md:min-w-0">
        <TableHeader>
          <TableRow>
            <TableHead>Email</TableHead>
          <TableHead>Role</TableHead>
          <TableHead>Invited By</TableHead>
          <TableHead>Sent</TableHead>
          <TableHead>Expires</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {invites.map((invite) => (
          <TableRow key={invite.id}>
            <TableCell>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{invite.email}</span>
              </div>
            </TableCell>
            <TableCell>{getRoleBadge(invite.role)}</TableCell>
            <TableCell>
              <span className="text-sm text-muted-foreground">
                {invite.invitedBy?.name || invite.invitedBy?.email || "Unknown"}
              </span>
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{formatDate(invite.createdAt)}</span>
              </div>
            </TableCell>
            <TableCell>
              <span className="text-sm text-muted-foreground">
                {formatDate(invite.expiresAt)}
              </span>
            </TableCell>
            <TableCell className="text-right">
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onResendInvite(invite.id)}
                  disabled={isResending}
                  title="Resend invitation email"
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Resend
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onCancelInvite(invite.id)}
                  disabled={isCancelling}
                  title="Cancel invitation"
                >
                  <X className="h-4 w-4 mr-1" />
                  Cancel
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    </div>
  );
}
